// src/pages/safety/FatigueTracking.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Clock, TrendingUp, AlertTriangle, CheckCircle, Battery, Shield } from 'lucide-react';
import { useSafety } from '../../contexts/SafetyContext.js';
import { useProgress } from '../../contexts/ProgressContext.js';

const FatigueTracking = () => {
  const navigate = useNavigate();
  const { getFatigueLevel, getSafetyScore } = useSafety();
  const { workoutHistory } = useProgress();
  
  const [fatigueLevel, setFatigueLevel] = useState(0);
  const [safetyScore, setSafetyScore] = useState(0);
  const [fatigueHistory, setFatigueHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInputForm, setShowInputForm] = useState(false);
  const [newFatigueLevel, setNewFatigueLevel] = useState(5);
  const [fatigueNote, setFatigueNote] = useState('');

  useEffect(() => {
    const loadFatigueData = () => {
      try {
        const currentFatigue = getFatigueLevel ? getFatigueLevel() : 5;
        const currentSafetyScore = getSafetyScore ? getSafetyScore() : 75;
        
        setFatigueLevel(currentFatigue);
        setSafetyScore(currentSafetyScore);
        
        // Generate sample fatigue history for demonstration
        const history = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          history.push({
            date: date.toISOString().split('T')[0],
            level: Math.max(1, Math.min(10, currentFatigue + (Math.random() - 0.5) * 2)),
            note: i === 0 ? 'Today' : `${i} days ago`
          });
        }
        setFatigueHistory(history);
        
      } catch (error) {
        console.error('Error loading fatigue data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFatigueData();
  }, [getFatigueLevel, getSafetyScore]);

  const getFatigueColor = (level) => {
    if (level <= 3) return 'text-green-600';
    if (level <= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getFatigueBgColor = (level) => {
    if (level <= 3) return 'bg-green-100';
    if (level <= 6) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getFatigueLabel = (level) => {
    if (level <= 3) return 'Low';
    if (level <= 6) return 'Moderate';
    return 'High';
  };

  const getFatigueRecommendations = (level) => {
    if (level <= 3) {
      return [
        'Great energy levels!',
        'Continue with your current routine',
        'Consider adding more challenging exercises'
      ];
    } else if (level <= 6) {
      return [
        'Moderate fatigue detected',
        'Ensure adequate rest between workouts',
        'Consider lighter intensity exercises'
      ];
    } else {
      return [
        'High fatigue levels detected',
        'Take additional rest days',
        'Focus on light stretching and recovery',
        'Consider consulting with a healthcare provider'
      ];
    }
  };

  const handleFatigueSubmit = (e) => {
    e.preventDefault();
    
    // Create new fatigue entry
    const newEntry = {
      date: new Date().toISOString().split('T')[0],
      level: newFatigueLevel,
      note: fatigueNote || 'No additional notes',
      timestamp: new Date().toISOString()
    };
    
    // Update fatigue history
    setFatigueHistory(prev => [newEntry, ...prev.slice(0, 6)]); // Keep last 7 entries
    
    // Update current fatigue level
    setFatigueLevel(newFatigueLevel);
    
    // Reset form
    setNewFatigueLevel(5);
    setFatigueNote('');
    setShowInputForm(false);
    
    // Show success message (you could add a toast notification here)
    alert(`Fatigue level ${newFatigueLevel} recorded successfully!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading fatigue tracking...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/safety')}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Safety Features
          </button>
          
          <div className="flex items-center gap-3 mb-4">
            <Heart className="text-red-500" size={32} />
            <h1 className="text-3xl font-bold text-gray-900">Fatigue Tracking</h1>
          </div>
          <p className="text-gray-600">
            Monitor your fatigue levels and get personalized recommendations for optimal recovery.
          </p>
        </div>

        {/* Current Fatigue Level */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Current Fatigue Level</h2>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getFatigueColor(fatigueLevel)} ${getFatigueBgColor(fatigueLevel)}`}>
                {fatigueLevel}/10 - {getFatigueLabel(fatigueLevel)}
              </span>
              <button
                onClick={() => setShowInputForm(!showInputForm)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                {showInputForm ? 'Cancel' : 'Update Fatigue Level'}
              </button>
            </div>
          </div>
          
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Fatigue Level</span>
              <span className="text-sm font-medium">{fatigueLevel}/10</span>
            </div>
            <div className="w-full bg-gray-200 h-3 rounded-full">
              <div 
                className={`h-3 rounded-full transition-all duration-300 ${
                  fatigueLevel <= 3 ? 'bg-green-500' 
                  : fatigueLevel <= 6 ? 'bg-yellow-500' 
                  : 'bg-red-500'
                }`} 
                style={{ width: `${fatigueLevel * 10}%` }}
              ></div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Battery className="text-gray-400" />
              <div>
                <p className="font-medium">Energy Level</p>
                <p className="text-sm text-gray-600">
                  {fatigueLevel <= 3 ? 'High' : fatigueLevel <= 6 ? 'Moderate' : 'Low'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="text-gray-400" />
              <div>
                <p className="font-medium">Recovery Status</p>
                <p className="text-sm text-gray-600">
                  {fatigueLevel <= 3 ? 'Well Rested' : fatigueLevel <= 6 ? 'Needs Rest' : 'Requires Rest'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Fatigue Input Form */}
        {showInputForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 border-2 border-blue-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Record Your Fatigue Level</h3>
            <form onSubmit={handleFatigueSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  How tired do you feel right now? (1 = Very energetic, 10 = Extremely tired)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newFatigueLevel}
                    onChange={(e) => setNewFatigueLevel(parseInt(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${getFatigueColor(newFatigueLevel)}`}>
                      {newFatigueLevel}
                    </div>
                    <div className="text-sm text-gray-600">
                      {getFatigueLabel(newFatigueLevel)}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Very Energetic</span>
                  <span>Extremely Tired</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={fatigueNote}
                  onChange={(e) => setFatigueNote(e.target.value)}
                  placeholder="e.g., 'Had a tough workout yesterday', 'Slept poorly last night', 'Feeling great today'"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Record Fatigue Level
                </button>
                <button
                  type="button"
                  onClick={() => setShowInputForm(false)}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Recommendations */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommendations</h3>
          <div className="space-y-3">
            {getFatigueRecommendations(fatigueLevel).map((recommendation, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  fatigueLevel <= 3 ? 'bg-green-500' 
                  : fatigueLevel <= 6 ? 'bg-yellow-500' 
                  : 'bg-red-500'
                }`}></div>
                <p className="text-gray-700">{recommendation}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Fatigue History */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Fatigue Levels</h3>
          <div className="space-y-3">
            {fatigueHistory.map((entry, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${
                    entry.level <= 3 ? 'bg-green-500' 
                    : entry.level <= 6 ? 'bg-yellow-500' 
                    : 'bg-red-500'
                  }`}></div>
                  <span className="font-medium">{entry.note}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">{entry.level}/10</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    entry.level <= 3 ? 'bg-green-100 text-green-700' 
                    : entry.level <= 6 ? 'bg-yellow-100 text-yellow-700' 
                    : 'bg-red-100 text-red-700'
                  }`}>
                    {entry.level <= 3 ? 'Low' : entry.level <= 6 ? 'Moderate' : 'High'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* How to Use Fatigue Tracking */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">How to Use Fatigue Tracking</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-800 mb-3">📊 Track Your Fatigue</h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">1</div>
                  <div>
                    <p className="font-medium text-gray-700">Before Each Workout</p>
                    <p className="text-sm text-gray-600">Rate your energy level from 1-10 before starting any exercise</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">2</div>
                  <div>
                    <p className="font-medium text-gray-700">After Each Workout</p>
                    <p className="text-sm text-gray-600">Note how tired you feel and any muscle soreness</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">3</div>
                  <div>
                    <p className="font-medium text-gray-700">Daily Check-ins</p>
                    <p className="text-sm text-gray-600">Track your overall energy and recovery each day</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-medium text-gray-800 mb-3">🎯 Understanding Your Levels</h4>
              <div className="space-y-3">
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="font-medium text-green-800">Low Fatigue (1-3)</span>
                  </div>
                  <p className="text-sm text-green-700">Great energy! You're well-rested and ready for challenging workouts.</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span className="font-medium text-yellow-800">Moderate Fatigue (4-6)</span>
                  </div>
                  <p className="text-sm text-yellow-700">Normal tiredness. Consider lighter exercises or more rest.</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="font-medium text-red-800">High Fatigue (7-10)</span>
                  </div>
                  <p className="text-sm text-red-700">Very tired. Focus on rest, light stretching, or skip workouts.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button 
              onClick={() => navigate('/safety')}
              className="p-4 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors text-left"
            >
              <div className="flex items-center mb-2">
                <Shield className="w-5 h-5 text-blue-600 mr-2" />
                <h4 className="font-medium text-blue-800">Safety Features</h4>
              </div>
              <p className="text-sm text-gray-600">Access all safety tools and settings</p>
            </button>
            
            <button 
              onClick={() => navigate('/dashboard')}
              className="p-4 border border-green-200 rounded-lg hover:bg-green-50 transition-colors text-left"
            >
              <div className="flex items-center mb-2">
                <TrendingUp className="w-5 h-5 text-green-600 mr-2" />
                <h4 className="font-medium text-green-800">Progress Dashboard</h4>
              </div>
              <p className="text-sm text-gray-600">View your overall fitness progress</p>
            </button>
          </div>
        </div>

        {/* Safety Score Impact */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Safety Score Impact</h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-gray-600 mb-2">Your current safety score:</p>
              <div className="flex items-center gap-3">
                <div className="text-3xl font-bold text-blue-600">{safetyScore}</div>
                <div className="text-sm text-gray-600">
                  {fatigueLevel <= 3 ? 'Fatigue levels are optimal for safety' : 
                   fatigueLevel <= 6 ? 'Moderate fatigue may affect safety' : 
                   'High fatigue may impact exercise safety'}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Fatigue Adjustment</p>
              <p className="text-lg font-semibold text-gray-900">
                {fatigueLevel <= 3 ? '+2.5' : fatigueLevel <= 6 ? '+1.0' : '-0.5'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FatigueTracking;
